import { Check } from '@mui/icons-material'
import { Box, Typography } from '@mui/material'
import { Trans } from 'react-i18next'
import { InfoPortControl } from './InfoPortControl'
import humanizeDuration from 'humanize-duration'
import { lang } from '../../utils/lang'
import { Port } from '../../types/models'
import { FwInfoConfig, FwInfoPort } from '../../types/ports'
import { DeviceControl } from '../DeviceControl'
import { now } from '../../utils/time'

/**
 * Controller for firmware info.
 */
export class FirmwareInfoPortControl extends InfoPortControl<FwInfoPort, FwInfoConfig> {

  port: FwInfoPort

  constructor(deviceControl: DeviceControl, port: Port) {
    super(deviceControl, port)
    this.port = port as FwInfoPort
  }

  getDescription(): string { return this.app.t('Information about the device firmware') }

  getTypeName(): string { return this.app.t('PortType-Firmware Info') }

  get group(): string { return this.app.t('PortGroup-Info') }

  infoTop() : React.ReactNode | undefined {
    const time = this.port.values.seconds_since_boot
    const flagDfuStarted = this.port.values.flag_dfu_started
    const flagDfuError = this.port.values.flag_dfu_error
    const firstMissingChunk = this.port.values.dfu_first_missing_chunk
    const rebootAt = time === undefined ? undefined : now().plus({ seconds: -time })

    return <Box>
      <Box><Trans>Last reboot</Trans>: {rebootAt === undefined ? '—' : rebootAt.toFormat('y-MM-d HH:mm')}</Box>
      <Box><Trans>Time sice last reboot</Trans>: {time === undefined ? '—' : time} <Trans>seconds</Trans></Box>
      <Box><Trans>Uptime</Trans>: {time === undefined ? '—' : humanizeDuration(time * 1000, { language: lang.current() })}</Box>

      { flagDfuStarted &&
        <Box sx={{ color: 'warning.main' }}>
          <Trans>Flashing started but not finished.</Trans><br/>
          <Trans>First missing chunk:</Trans> {firstMissingChunk || '—'}
        </Box>
      }
      { flagDfuError &&
        <Box sx={{ color: 'error.main' }}>
          <Trans>Flashing finished with an error.</Trans>
        </Box>
      }
    </Box>
  }

  infoLeft(): React.ReactNode | undefined {
    const inUse = !this.port.values.flag_fw_b_in_use
    const confAInUse = !this.port.values.flag_conf_b_in_use

    return (
      <Box>
        <Typography sx={{ color: 'white' }}>
          <Trans>Firmware A</Trans>
          {inUse ? <Check color="success" sx={{ ml: '0.2rem', mb: 1, fontSize: '1rem' }}/> : <></>}
        </Typography>
        {this.valueView(this.app.t('Firmware Version'), 'fw_a_version')}
        {this.valueView(this.app.t('Firmware CRC'), 'fw_a_crc')}
        {this.valueView(this.app.t('Firmware Valid'), 'flag_fw_a_valid')}
        {this.valueView(this.app.t('Firmware Update Count'), 'fw_a_update_cnt')}

        { inUse && <>
          <Typography variant="h5" sx={{ mt: 2, mb: 1 }}>
            <Trans>Configuration</Trans>
          </Typography>
          {this.valueView(this.app.t('Configuration A Valid'), 'flag_conf_a_valid', confAInUse)}
          {this.valueView(this.app.t('Configuration B Valid'), 'flag_conf_b_valid', !confAInUse)}
          </>
        }
      </Box>
    )
  }

  infoRight(): React.ReactNode | undefined {
    const inUse = !!this.port.values.flag_fw_b_in_use
    const confAInUse = !this.port.values.flag_conf_b_in_use

    return (
      <Box>
        <Typography sx={{ color: 'white' }}>
          <Trans>Firmware B</Trans>
          {inUse ? <Check color="success" sx={{ ml: '0.2rem', mb: 1, fontSize: '1rem' }}/> : <></>}
        </Typography>
        {this.valueView(this.app.t('Firmware Version'), 'fw_b_version')}
        {this.valueView(this.app.t('Firmware CRC'), 'fw_b_crc')}
        {this.valueView(this.app.t('Firmware Valid'), 'flag_fw_b_valid')}
        {this.valueView(this.app.t('Firmware Update Count'), 'fw_b_update_cnt')}
        { inUse && <>
          <Typography variant="h5" sx={{ mt: 2, mb: 1 }}>
            <Trans>Configuration</Trans>
          </Typography>
          {this.valueView(this.app.t('Configuration A Valid'), 'flag_conf_a_valid', confAInUse)}
          {this.valueView(this.app.t('Configuration B Valid'), 'flag_conf_b_valid', !confAInUse)}
          </>
        }
      </Box>
    )
  }
}
